import { Menu } from "@etudescarre/ui";
import { isNotNil } from "@etudescarre/utils";
import {
  KeyIcon,
  SignOutIcon,
  UserIcon,
  UserSwitchIcon,
} from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import { authClient } from "@/lib/auth-client";
import {
  confirmLeaveWithUnsavedChanges,
  useGuardedNavigate,
} from "@/lib/utils/unsaved-changes";

export const Header = () => {
  const navigate = useNavigate();
  const guardedNavigate = useGuardedNavigate();
  const session = authClient.useSession();
  const [stoppingImpersonation, setStoppingImpersonation] = useState(false);
  const [impersonation, setImpersonation] = useState<{
    write: boolean;
    impersonatorEmail: string | null;
    passwordCreated: boolean;
    hasPreparationYards: boolean;
  } | null>(null);

  const isImpersonating = Boolean(session.data?.session.impersonatedBy);

  useEffect(() => {
    if (!isImpersonating) {
      setImpersonation(null);
      return;
    }
    apiClient.GET("/api/impersonation/mode").then(({ data }) => {
      if (data) setImpersonation(data.data);
    });
  }, [isImpersonating]);

  const handleLogout = () => {
    guardedNavigate("/auth/sign-out");
  };

  const handleStopImpersonating = async () => {
    if (!(await confirmLeaveWithUnsavedChanges())) return;
    setStoppingImpersonation(true);
    try {
      // The staff guard must see the restored account before /staff renders,
      // or it refuses the page. Without disableSignal, better-auth refetches
      // the session 10 ms later and that fetch aborts the awaited one below.
      const { error } = await authClient.admin.stopImpersonating({
        fetchOptions: { disableSignal: true },
      });
      if (error) {
        toaster.error({
          title: "Erreur",
          description: "Impossible de revenir à votre compte",
        });
        return;
      }
      await session.refetch();
      navigate("/staff");
    } finally {
      setStoppingImpersonation(false);
    }
  };

  // What the user could not do yet on their own, anticipated by staff and
  // stated in the banner.
  const anticipations: string[] = [];
  if (isNotNil(impersonation) && !impersonation.passwordCreated) {
    anticipations.push(
      "Anticipation : ce compte n'a pas encore de mot de passe et ne peut pas encore se connecter lui-même.",
    );
  }
  if (isNotNil(impersonation) && impersonation.hasPreparationYards) {
    anticipations.push(
      "Anticipation : ses chantiers en préparation s'ouvrent avant leur mise en service.",
    );
  }

  return (
    <div className="sticky top-0 z-40 border-gray-200 border-b bg-white">
      {isImpersonating && session.data?.user && (
        <div className="bg-amber-100 px-4 py-1.5 text-amber-900 text-sm">
          <div className="flex items-center justify-center gap-3">
            <UserSwitchIcon className="h-4 w-4" />
            <span>
              Vous incarnez{" "}
              <strong>
                {session.data.user.name || session.data.user.email}
              </strong>
              {isNotNil(impersonation) &&
                (impersonation.write ? " (avec écriture)" : " (lecture seule)")}
            </span>
            <button
              type="button"
              onClick={handleStopImpersonating}
              disabled={stoppingImpersonation}
              className="cursor-pointer font-medium underline hover:text-amber-700 disabled:opacity-50"
            >
              {stoppingImpersonation ? "Retour..." : "Revenir à mon compte"}
            </button>
          </div>
          {anticipations.map((text) => (
            <p key={text} className="text-center text-xs">
              {text}
            </p>
          ))}
        </div>
      )}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <button
            type="button"
            className="cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => guardedNavigate("/")}
          >
            <img src="/logo.png" alt="ChantierCarré" className="h-10" />
          </button>
          <div className="flex items-center gap-4">
            {session.data?.user && (
              <Menu positioning={{ placement: "bottom-end" }}>
                <Menu.Trigger plain className="!items-center">
                  <UserIcon className="h-4 w-4" data-slot="icon" />
                  {isImpersonating
                    ? impersonation?.impersonatorEmail
                    : session.data.user.email}
                </Menu.Trigger>
                <Menu.Content>
                  {!isImpersonating && (
                    <Menu.Item
                      value="change-password"
                      onClick={() => {
                        guardedNavigate("/auth/change-password");
                      }}
                    >
                      <KeyIcon className="h-4 w-4" />
                      Modifier le mot de passe
                    </Menu.Item>
                  )}
                  <Menu.Item value="logout" onClick={handleLogout}>
                    <SignOutIcon className="h-4 w-4" />
                    Déconnexion
                  </Menu.Item>
                </Menu.Content>
              </Menu>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
